import { create } from 'zustand';
import { AthleteStatsState } from './types';
import { supabase } from '@/integrations/supabase/client';
import { addDays, format, differenceInCalendarDays, isValid } from 'date-fns';
import { getUserGymId } from '@/utils/gymAuth';
import { getCurrentUserId } from '@/utils/authHelpers';

export const useAthleteStatsStore = create<AthleteStatsState>((set) => ({
  totalClasses: 0,
  averageRating: 0,
  streak: 0,
  longestStreak: 0,
  totalHours: 0,
  isLoading: false,
  error: null,
  bookingHistory: [] ,
  
  fetchAthleteStats: async () => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      
      const userId = await getCurrentUserId();
      
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Calculate start and end dates for bookings
      const now = new Date();
      const sixMonthsAgo = addDays(now, -365);
      
      const { data: bookings, error: bookingsError } = await supabase
        .from('class_bookings')
        .select(`
          id,
          class_date,
          status,
          time,
          scheduled_classes (
            duration
          )
        `)
        .eq('user_id', userId)
        .eq('gym_id',gymId )
        .gte('class_date', sixMonthsAgo.toISOString())
        .order('class_date', { ascending: true });
      
      if (bookingsError) throw bookingsError;
      
      if (!bookings) {
        set({ 
          totalClasses: 0,
          averageRating: 0,
          streak: 0,
          longestStreak: 0,
          totalHours: 0
        });
        return;
      }
      
      let totalMinutes = 0;

      const completedBookings = bookings.filter(b => b.status === 'completed');

      // Jours DISTINCTS : deux séances le même jour cassaient la série
      // (l'écart entre elles n'étant pas de 1 jour) — cf. audit 4.4.
      const distinctSessionDays = new Set<string>();

      completedBookings.forEach(booking => {
        totalMinutes += parseInt(booking.scheduled_classes?.duration || '60');

        const bookingDate = new Date(booking.class_date);
        if (isValid(bookingDate)) {
          distinctSessionDays.add(format(bookingDate, 'yyyy-MM-dd'));
        }
      });

      const sortedDays = Array.from(distinctSessionDays).sort();
      let maxStreak = 0;
      let runLength = 0;
      let previousDay: Date | null = null;
      for (const day of sortedDays) {
        const dayDate = new Date(`${day}T00:00:00`);
        runLength = previousDay && differenceInCalendarDays(dayDate, previousDay) === 1
          ? runLength + 1
          : 1;
        maxStreak = Math.max(maxStreak, runLength);
        previousDay = dayDate;
      }

      // Série EN COURS = celle qui se termine aujourd'hui ou hier. Sinon 0 :
      // afficher « série en cours : 3 » pour un historique vieux de six mois
      // était trompeur.
      const today = new Date(`${format(new Date(), 'yyyy-MM-dd')}T00:00:00`);
      const currentStreak = previousDay && differenceInCalendarDays(today, previousDay) <= 1
        ? runLength
        : 0;
      
      set({
        totalClasses: completedBookings.length,
        averageRating: 0, // Will be implemented when ratings are added
        streak: currentStreak,
        longestStreak: maxStreak,
        totalHours: Math.round(totalMinutes / 60),
        bookingHistory: completedBookings
      });
      
    } catch (error) {
      console.error('Error fetching athlete stats:', error);
      set({ error: 'Failed to fetch athlete stats' });
    } finally {
      set({ isLoading: false });
    }
  }

}
));
